/**
 * KanbanBoard.tsx — Pipeline de ventas REAL (Nuevo→Contactado→Propuesta→Cerrado),
 * persistido en el backend propio `/api/v1/listings/pipeline` (KANBAN-PIPELINE).
 *
 * - Recibe las tarjetas YA cargadas (GET /pipeline) agrupadas aquí por `stage`.
 * - Arrastrar una tarjeta a otra columna → `onMove(place_id, stage)` (el
 *   contenedor hace PATCH con UI optimista). dnd-kit: tarjetas draggable,
 *   columnas droppable; el click (sin arrastre) abre la ficha.
 * - Botón "sacar" en la tarjeta → `onRemove(place_id)` (DELETE).
 * - Tarjeta: categoría EN ESPAÑOL (categoryLabel), zona (address_components),
 *   Opportunity Score y señales — estética cabina, como el resto.
 */
import { useMemo, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core'
import type {
  PipelineCard,
  PipelineStage,
  PipelineOrderBy,
  NextStepStatus,
} from '@/services/listingsService'
import { opportunityScore, leadSignals } from '@/lib/opportunityScore'
import { categoryLabel } from '@/lib/categoryLabel'
import { formatFollowUp } from '@/lib/followUp'

const stroke = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const
const SEM_GLYPH = { high: '✓', mid: '!', low: '✕' } as const

/** Iconos por estado del follow-up (calendario; aviso para "sin próximo paso"). */
const calIcon = (
  <svg viewBox="0 0 24 24" {...stroke}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>
)
const warnIcon = (
  <svg viewBox="0 0 24 24" {...stroke}><path d="M12 9v4M12 17h.01M10.3 4.3L2.5 18a2 2 0 001.7 3h15.6a2 2 0 001.7-3L13.7 4.3a2 2 0 00-3.4 0z" /></svg>
)

const ORDER_LABELS: Record<PipelineOrderBy, string> = {
  next_action: 'Próxima acción',
  added: 'Añadido al pipeline',
  score: 'Opportunity Score',
}

/** Pill de follow-up de la tarjeta, coloreado por `next_step_status`.
 *  La FECHA manda: hay próximo paso sii `status !== 'no_next_step'`. El texto es
 *  opcional; si falta, se muestra una etiqueta por defecto (no "Sin próximo paso"). */
function FollowUpPill({ card }: { card: PipelineCard }) {
  const status: NextStepStatus = card.next_step_status ?? 'no_next_step'
  if (status === 'no_next_step') {
    return (
      <span className="fu-pill no_next_step">{warnIcon}<span className="lbl">Sin próximo paso</span></span>
    )
  }
  const when = formatFollowUp(card.next_step_due, status)
  const label = card.next_step_text?.trim() || 'Próxima acción'
  return (
    <span className={`fu-pill ${status}`}>
      {calIcon}
      <span className="lbl">{label}</span>
      {when && <> · <span className="fu-when">{when}</span></>}
    </span>
  )
}

const COLS: { key: PipelineStage; label: string }[] = [
  { key: 'nuevo', label: 'Nuevo' },
  { key: 'contactado', label: 'Contactado' },
  { key: 'propuesta', label: 'Propuesta' },
  { key: 'cerrado', label: 'Cerrado' },
]
const STAGE_SET = new Set<string>(COLS.map((c) => c.key))

const grip = (
  <span className="grip" aria-hidden="true"><svg viewBox="0 0 24 24" {...stroke}><circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" /><circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" /></svg></span>
)

function cardZone(c: PipelineCard): string {
  return c.address_components?.city || c.address_components?.region || ''
}
function cardCategory(c: PipelineCard): string {
  return categoryLabel(c.category_primary ?? c.category_ids?.[0] ?? null)
}

/** Contenido visual de una tarjeta (compartido por la tarjeta y el overlay). */
function CardBody({ card, onRemove }: { card: PipelineCard; onRemove?: (id: string) => void }) {
  const sc = opportunityScore(card)
  const sigs = leadSignals(card).slice(0, 2)
  const zone = cardZone(card)
  return (
    <>
      <div className="kc-top">
        <div style={{ minWidth: 0 }}>
          <div className="kc-name">{card.title || '—'}</div>
          <div className="kc-cat">{cardCategory(card)}{zone ? ` · ${zone}` : ''}</div>
        </div>
        <span className={`oc-score ${sc.band}`}><span className={`sem ${sc.band}`}>{SEM_GLYPH[sc.band]}</span><b>{sc.score}</b></span>
      </div>
      {sigs.length > 0 && (
        <div className="kc-sigs">{sigs.map((s) => <span className={`sig ${s.kind}`} key={s.label}>{s.label}</span>)}</div>
      )}
      <div className="kc-fu">
        <FollowUpPill card={card} />
        {onRemove && (
          <button
            type="button"
            className="kc-remove"
            title="Sacar del pipeline"
            aria-label="Sacar del pipeline"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onRemove(card.place_id) }}
          >
            <svg viewBox="0 0 24 24" {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        )}
        {grip}
      </div>
    </>
  )
}

function DraggableCard({
  card,
  onOpenLead,
  onRemove,
}: {
  card: PipelineCard
  onOpenLead: (c: PipelineCard) => void
  onRemove: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: card.place_id })
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.35 : 1,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="kcard"
      onClick={() => onOpenLead(card)}
      {...attributes}
      {...listeners}
    >
      <CardBody card={card} onRemove={onRemove} />
    </div>
  )
}

function Column({
  col,
  cards,
  onOpenLead,
  onRemove,
}: {
  col: { key: PipelineStage; label: string }
  cards: PipelineCard[]
  onOpenLead: (c: PipelineCard) => void
  onRemove: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key })
  return (
    <div className={`kcol${isOver ? ' kcol-over' : ''}`} ref={setNodeRef}>
      <header>
        <span className={`kdot ${col.key}`} />
        {col.label}
        <span className="kc-n">{cards.length}</span>
      </header>
      {cards.length === 0 ? (
        <div className="kghost">Arrastra aquí</div>
      ) : (
        cards.map((c) => (
          <DraggableCard key={c.place_id} card={c} onOpenLead={onOpenLead} onRemove={onRemove} />
        ))
      )}
    </div>
  )
}

export default function KanbanBoard({
  cards,
  loading,
  error,
  orderBy,
  onOrderBy,
  onOpenLead,
  onMove,
  onRemove,
  onAddLeads,
}: {
  cards: PipelineCard[]
  loading: boolean
  error: string | null
  orderBy: PipelineOrderBy
  onOrderBy: (o: PipelineOrderBy) => void
  onOpenLead: (c: PipelineCard) => void
  onMove: (placeId: string, stage: PipelineStage) => void
  onRemove: (placeId: string) => void
  onAddLeads?: () => void
}) {
  const [activeId, setActiveId] = useState<string | null>(null)

  // Avisos de seguimiento de un vistazo (alimenta las pills de la barra).
  const overdueCount = useMemo(() => cards.filter((c) => c.next_step_status === 'overdue').length, [cards])
  const noStepCount = useMemo(() => cards.filter((c) => c.next_step_status === 'no_next_step').length, [cards])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  const stageById = useMemo(() => {
    const m = new Map<string, PipelineStage>()
    for (const c of cards) m.set(c.place_id, c.stage)
    return m
  }, [cards])

  const buckets = useMemo(() => {
    const b: Record<PipelineStage, PipelineCard[]> = { nuevo: [], contactado: [], propuesta: [], cerrado: [] }
    for (const c of cards) (b[c.stage] ?? b.nuevo).push(c)
    return b
  }, [cards])

  const activeCard = activeId ? cards.find((c) => c.place_id === activeId) ?? null : null

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id))
  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = e
    if (!over) return
    const id = String(active.id)
    const overId = String(over.id)
    const target = STAGE_SET.has(overId) ? (overId as PipelineStage) : stageById.get(overId)
    if (target && target !== stageById.get(id)) onMove(id, target)
  }

  return (
    <div className="view active" id="view-kanban">
      <div className="kanban-cmd">
        <label className="sortdrop">
          <span className="lab">Ordenar por</span> <b>{ORDER_LABELS[orderBy]}</b>
          <svg viewBox="0 0 24 24" {...stroke}><path d="M6 9l6 6 6-6" /></svg>
          <select
            value={orderBy}
            onChange={(e) => onOrderBy(e.target.value as PipelineOrderBy)}
            aria-label="Ordenar el pipeline por"
          >
            <option value="next_action">Próxima acción</option>
            <option value="added">Añadido al pipeline</option>
            <option value="score">Opportunity Score</option>
          </select>
        </label>
        {overdueCount > 0 && (
          <span className="cmd-alert danger"><span className="n">{overdueCount}</span>{overdueCount === 1 ? 'follow-up vencido' : 'follow-ups vencidos'}</span>
        )}
        {noStepCount > 0 && (
          <span className="cmd-alert warn"><span className="n">{noStepCount}</span>sin próximo paso</span>
        )}
        {loading && <span className="kanban-sync">Cargando tu pipeline…</span>}
        {onAddLeads && (
          <>
            <span className="cmd-spacer" />
            <button type="button" className="add-btn" onClick={onAddLeads}>
              <svg viewBox="0 0 24 24" {...stroke}><path d="M12 5v14M5 12h14" /></svg>
              Añadir leads
            </button>
          </>
        )}
      </div>

      {error ? (
        <div className="kanban-empty">{error}</div>
      ) : !loading && cards.length === 0 ? (
        <div className="kanban-empty">
          Tu pipeline está vacío. Añade leads desde su ficha o seleccionándolos en la tabla.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <div className="kanban">
            {COLS.map((col) => (
              <Column
                key={col.key}
                col={col}
                cards={buckets[col.key]}
                onOpenLead={onOpenLead}
                onRemove={onRemove}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard ? (
              <div className="kcard kcard-overlay"><CardBody card={activeCard} /></div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
